import { useEffect, useRef } from "react";
import { Button, Screen, Spinner, Text, useDelayedFlag } from "@ferrivia/ui";
import { useSignIn, useTracker, type SessionCredentials } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import s from "./LoginScreen.module.css";
import { useScreenBackground } from "../../hooks/useScreenBackground";
import { InstallPrompt } from "../InstallPrompt";

export interface LoginScreenProps {
  initialErrorCode?: string | null;
  onSignedIn: (credentials: SessionCredentials) => void;
}

export function LoginScreen({ initialErrorCode = null, onSignedIn }: LoginScreenProps) {
  const tracker = useTracker();
  const { t } = useTranslation("client");
  const { pending, error, signIn } = useSignIn({ onSignedIn, initialErrorCode, track: tracker });
  // Delayed so a fast (cache-warm) sign-in never shows it — only a cold-start
  // stall that outlasts the delay does.
  const showSpinner = useDelayedFlag(pending, 400);

  useScreenBackground("login", "--color-surface-default");

  // A ref, not a dep: tracker's identity can change but this view event
  // must still fire exactly once per mount, not once per identity change.
  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;

  useEffect(() => {
    trackerRef.current.track("signin.screen_viewed", {});
  }, []);

  return (
    <Screen className={s.root}>
      <div className={s.brandmark}>
        <Text as="h1" size="3xl" className={s.name}>
          Ferrivia
        </Text>
        <div className={s.rule} aria-hidden="true" />
        <Text as="p" className={s.tagline}>
          {t("login.tagline", "Raudanpuutteen digiklinikka")}
        </Text>
      </div>
      <div className={s.actions}>
        <Button className={s.submit} disabled={pending} onClick={signIn}>
          {showSpinner ? <Spinner size={16} /> : null}
          {t("login.submit", "Kirjaudu sisään")}
        </Button>
      </div>
      {error ? (
        <Text as="p" size="sm" className={s.error}>
          {error}
        </Text>
      ) : null}
      <InstallPrompt />
    </Screen>
  );
}
